import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, Renderer2, ChangeDetectorRef } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, Subject, filter, take, takeUntil } from 'rxjs';
import { Library } from '../../models/libraryModel/library.model';
import { AddRow, SetLibrary, ModifyRow, UndoAction, RedoAction, LibrarySaved, MarkRowToDelete, DeleteRows, AddRowFromClipboard, MoveRowToPosition, SortRowsAlphabetically } from '../../ngrx/libraries/library.actions';
import { AppState } from '../../app.state';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ModifiedLibrary } from '../../models/libraryModel/modifiedLibrary.model';
import { ExtendedRow } from '../../models/libraryModel/extendedRow.model';
import { LibSvc } from 'src/app/services/libService/libSvc.service';
import { tabGroupMapping } from 'src/app/services/tabGroupMapping';
import { SweetAlertService } from 'src/app/services/alerts/sweetAlert.service';

@Component({
  selector: 'library-table',
  templateUrl: './library-table.component.html',
  styleUrls: ['./library-table.component.css'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager
})
export class LibraryTableComponent implements OnInit, OnDestroy {

  // Define observables and variables
  library$: Observable<ModifiedLibrary & 
  {
    allowedTabNames: string[], 
    allowedRows: (ExtendedRow & {order: number})[], 
    initialRows: ExtendedRow[],
    selectedRowsIdsToDelete: Set<number>,
    modifications: {added: ExtendedRow[], modified: ExtendedRow[], deleted: ExtendedRow[]}, 
    past: any[], 
    future: any[],
    // orderBy: {columnName: string, order: "asc" | "desc"}
  }> = this.store.pipe(select('library'));

  libraryName: string = "";
  isLoading: boolean = false;

  pageSize = 20;
  totalPages: number = 0;
  currentPage = 0;

  hasMultipleTabNames: boolean = true;
  allowedTabNames: string[] = [];
  columns: {columnName: string, type: string, unit?: string | null}[] = [];
  selectedTabName: string = '';
  searchTerm: string = '';
  displayedRows: (ExtendedRow & {order: number})[] = [];
  editedRow: { [key: string]: any } = {};
  errorMessage: string = '';
  currentRowId: number = -1;  
  addedRowsIds: Number[] = [];
  modifiedRowsIds: Number[] = [];
  selectedRowsIdsToDelete: Set<number> = new Set<number>();
  orderingEnabled: boolean = false;
  advancedOrdering: boolean = false;
  orderingColumn: string = '';
  orderingDirection: 'asc' | 'desc' = 'asc';
  mode: string = '';

  // Cancels any in-flight library load requests (getLibGroups/getLibrary) so that a stale
  // response from a previously selected unit set can never overwrite a newer one.
  private cancelPendingLibraryLoad$ = new Subject<void>();

  // 10 UDL unit groups supported by the library API
  unitSets: string[] = ['US', 'Metric', 'EU', 'IBU', 'JO_PZ', 'MCBU-HWC', 'Mixed', 'NMA', 'RBU', 'TCO_BB'];
  selectedUnitSet: string = '';

  // Constructor to initialize services and dependencies
  constructor(
    private store: Store<AppState>, 
    private libraryService: LibSvc, 
    private router: Router, 
    private route: ActivatedRoute, 
    private sweetAlertService: SweetAlertService,
    private cdr: ChangeDetectorRef
  ) {
  }

  // Lifecycle hook to initialize the component
  ngOnInit() {    
    this.updateLibrary()

    // Subscribe to router events to update the library on navigation
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.updateLibrary()
    });   
  }

  ngOnDestroy() {
    this.cancelPendingLibraryLoad$.next();
    this.cancelPendingLibraryLoad$.complete();
  }

  // Function to update the library data
  updateLibrary() {
    // Cancel any previously started, still-pending library load so its response
    // (e.g. from a unit set that was quickly switched away from) can't be applied later.
    this.cancelPendingLibraryLoad$.next();

    // Retrieve the library name from the route parameters
    this.libraryName = this.route.snapshot.paramMap.get('libraryName') ?? "";

    this.selectedTabName = '';
    this.editedRow = {};
    this.currentPage = 0;
    this.searchTerm = "";
    this.errorMessage = '';

    if (this.libraryName === ":libraryName"){      
      this.router.navigate(['/lib-editor', '']);
    }

    if (this.libraryName !== "" && this.libraryName !== ":libraryName") {      
      this.isLoading = true;

      // Fetch user groups
      console.log(`[LibraryTable] waiting on getLibGroups() for '${this.libraryName}'...`);
      this.libraryService.getLibGroups().pipe(takeUntil(this.cancelPendingLibraryLoad$)).subscribe({
        next: (userGroups) => {
          console.log(`[LibraryTable] getLibGroups() resolved with ${userGroups.length} groups`);

        if (
          !(this.libraryService.isSuperAdmin() || this.libraryService.isAdmin()) &&
          !(
            tabGroupMapping[this.libraryName] && 
            Object.keys(tabGroupMapping[this.libraryName])
              .some(tab => tabGroupMapping[this.libraryName][tab]
                .some(group => userGroups.includes(group)))
          )
        ) {
          this.errorMessage = 'You do not have access to this library';
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }


        // Fetch the library data from the service
        console.log(`[LibraryTable] waiting on getLibrary('${this.libraryName}')...`);
        this.libraryService.getLibrary(this.libraryName, this.selectedUnitSet).pipe(takeUntil(this.cancelPendingLibraryLoad$)).subscribe(library => {
          console.log(`[LibraryTable] getLibrary() resolved with ${library.rows.length} rows`);
        
          // Dispatch an action to set the library data in the store
          const showAllTabs = this.libraryService.isSuperAdmin() || this.libraryService.isAdmin();;
          this.store.dispatch(SetLibrary({ library, userGroups, showAllTabs }));
          
          this.orderingEnabled = this.libraryService.isSuperAdmin();
          this.orderingColumn = library.columns[0].columnName;
          this.mode = this.libraryService.isSuperAdmin() ? ' (Super admin mode)' : this.libraryService.isAdmin() ? ' (Admin mode)' : '';

          // Subscribe to the library observable to update component state
          this.library$.pipe(takeUntil(this.cancelPendingLibraryLoad$)).subscribe(lib => {
            this.allowedTabNames = lib.allowedTabNames;
            this.columns = lib.columns;
            this.hasMultipleTabNames = lib.tabNames.filter(tabName => tabName !== "").length > 0;  
            
            if (lib.tabNames.filter(tabName => tabName !== "").length > 0 && lib.allowedTabNames.length === 0) {
              this.errorMessage = 'You do not have access to any tabs in this library';
            }
            
            this.addedRowsIds = lib.modifications.added.map(row => row.id);
            this.modifiedRowsIds = lib.modifications.modified.map(row => row.id);
            this.selectedRowsIdsToDelete = lib.selectedRowsIdsToDelete;
            
            this.updateDisplayedRows();            
            this.isLoading = false;
            console.log('[LibraryTable] isLoading set to false, forcing change detection');
            this.cdr.detectChanges();
          })
        });
      },
      error: error => {
        if (error.status === 404) {
          this.errorMessage = 'Failed to load resource: the server responded with a status of 404 (Not Found)';
        } else if (error.status === 401) {
          this.errorMessage = 'Unauthorized access: the server responded with a status of 401 (Unauthorized)';
        } else if (error.status === 500) {
          this.errorMessage = 'Internal server error: the server responded with a status of 500 (Internal Server Error)';
        } else {
          this.errorMessage = 'An unexpected error occurred';
        }
        this.isLoading = false;
        this.cdr.detectChanges();
      }});
    }
  }

  // Function to update the displayed rows based on filters and pagination
  updateDisplayedRows() {
    // Subscribe to the library observable to get the current state of the library
    this.library$.pipe(take(1)).subscribe(lib => {      
      let filteredRows = lib.allowedRows
      .filter(row => (this.selectedTabName === "" || row.tabName === this.selectedTabName) && this.filterRow(row))

      // Calculate the total number of pages based on the filtered rows and page size
      this.totalPages = Math.ceil(filteredRows.length / this.pageSize);

      // Update the displayed rows based on the current page and page size
      this.displayedRows = filteredRows
        .slice(this.currentPage * this.pageSize, (this.currentPage + 1) * this.pageSize);   
    })
  }
  
  // Function to filter all the rows fields based on search term
  filterRow(row: ExtendedRow): boolean {
    // Create an array of field names to exclude
    const excludedFields = ['id', 'tabName', 'order'];

    // Concatenate the values of the fields that are not excluded
    const concatenatedFields = Object.keys(row)
      .filter(key => !excludedFields.includes(key))
      .map(key => row[key])
      .join(' ')
      .toLowerCase();

    return concatenatedFields.includes(this.searchTerm.toLowerCase());
  }

  getRowColor(rowId: number): string {
    if (this.currentRowId === rowId) {
      return '#d9e4f5';
    } else if (Array.from(this.selectedRowsIdsToDelete).some(id => id === rowId)) {
      return '#f8c9cd';
    } else if (this.addedRowsIds.includes(rowId)) {
      return '#bffacd';
    } else if (this.modifiedRowsIds.includes(rowId)) {
      return '#faecbd';
    }
    return '';
  }

  // Function to add a new row
  addRow(tabName: string) {
    if (this.hasMultipleTabNames && tabName === "")
      return

    this.currentPage = 0
    this.store.dispatch(AddRow({ tabName }));
  }

  processClipboardData() {
  
    if (!navigator.clipboard) {
      console.log('Clipboard API not supported in your browser.');
      return;
    }

    navigator.clipboard.readText().then((clipboardText) => {

      if (!clipboardText.trim()) {
        console.log('Clipboard is empty!');
        return;
      }

      try {
        const rows = clipboardText.replace(/[\r\n]+$/, '').split('\r\n').map(row => row.split('\t'));

        console.log(clipboardText);        
        console.log(rows);

        this.store.dispatch(AddRowFromClipboard({ tabName: this.selectedTabName, rows }));        
      } catch (error) {
        console.error('Error parsing clipboard data:', error);
        return;
      }
    });
  }

  onRowSelectionChange(rowId: number, event: Event) {
    const checkbox = event.target as HTMLInputElement;
    this.store.dispatch(MarkRowToDelete({ id: rowId, status: checkbox.checked }));    
  }

  onInputFocus(id: number) {
    this.currentRowId = id;
  }

  // Event handler for input change in a row
  // saves the changes done in the field
  onInputChange(id: number, key: string, value: any) {
    if (!this.editedRow[id]) {
      this.editedRow[id] = {};
    }
    this.editedRow[id][key] = value; 
  }

  // Event handler for input blur event in a row
  // takes the changes saved and updates the reducer
  onInputBlur(id: number, key: string) {
    if (this.editedRow[id] && this.editedRow[id][key] !== undefined) {      
      this.store.dispatch(ModifyRow({ id, key, value:this.editedRow[id][key].trim()}));
      delete this.editedRow[id][key];
    }
    this.currentRowId = -1
  }
  
  onOrderChange(id: number, event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    var newPosition = Number(selectElement.value);

    if (newPosition < 0)
      newPosition = 0
    if (newPosition > this.displayedRows.length - 1)
      newPosition = this.displayedRows.length - 1

    this.store.dispatch(MoveRowToPosition({ id, newPosition }));
  }

  sortRows() {
    this.store.dispatch(SortRowsAlphabetically({ tabName: this.selectedTabName, columnName: this.orderingColumn, direction: this.orderingDirection }));
  }

  // Event handler for tab filter change
  onTabChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    this.selectedTabName = selectElement.value;
    this.currentPage = 0;
    this.updateDisplayedRows()
  }

  // Event handler for unit set change, reloads the library data with the newly selected unit set
  onUnitSetChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    const newUnitSet = selectElement.value;
    const previousUnitSet = this.selectedUnitSet;

    this.library$.pipe(take(1)).subscribe(lib => {
      if ((lib.past?.length ?? 0) > 0) {
        // Revert the dropdown until the user decides what to do, so the displayed
        // value doesn't get out of sync with selectedUnitSet.
        selectElement.value = previousUnitSet;

        this.sweetAlertService.confirmDiscardChanges(
          'You have unsaved changes. Please save before switching unit sets, or your changes will be lost.'
        ).then((result) => {
          if (result.isConfirmed) {
            selectElement.value = newUnitSet;
            this.selectedUnitSet = newUnitSet;
            this.updateLibrary();
          }
        });
        return;
      }

      this.selectedUnitSet = newUnitSet;
      this.updateLibrary();
    });
  }

  // Event handler for search input filter change
  onSearch(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    this.searchTerm = selectElement.value;
    this.currentPage = 0;
    this.updateDisplayedRows();
  }

  onPageChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    this.currentPage = Number(selectElement.value);
    this.updateDisplayedRows();
  }

  // Function to navigate to the previous page in pagination
  previousPage() {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.updateDisplayedRows();
    }
  }

  // Function to navigate to the next page in pagination
  nextPage() {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
      this.updateDisplayedRows();
    }
  }

  // Function to undo last action
  undo() {
    this.store.dispatch(UndoAction()); 
    this.updateDisplayedRows()
  }

  // Function to redo last undone action
  redo() {
    this.store.dispatch(RedoAction()); 
    this.updateDisplayedRows()
  }

  // Function to submit the library data
  submitLibrary() {
    // Subscribe to the library$ observable and retrieve the library data
    this.library$.pipe(take(1)).subscribe((library) => {
      this.libraryService.getLibrary(this.libraryName, this.selectedUnitSet).subscribe((latestLibrary) => {
        
        if (latestLibrary.rows.length !== library.initialRows.length || latestLibrary.rows.some((latestRow: ExtendedRow, index) => {          
          return Object.keys(latestRow).some(key => latestRow[key] !== library.initialRows[index][key]);
        })) {
          this.sweetAlertService.showErrorMessage('The library has been modified by another user. Please refresh the page and re-edit. You will lose your changes.');
          return;
        }

        // Generate the modifications table
        const modificationsTable = this.generateModificationsTableHTMLString(library);

        this.sweetAlertService.confirmSaveChanges(modificationsTable).then((result) => {
          if (result.isConfirmed) {
            if (library) {

              this.store.dispatch(DeleteRows());

              this.library$.pipe(take(1)).subscribe((libraryWithRowsDeleted) => {

                this.isLoading = true;
                this.updateDisplayedRows();
      
                console.log('Submitting library:', libraryWithRowsDeleted);
      
                this.libraryService.putLibrary(libraryWithRowsDeleted, !this.advancedOrdering, this.selectedUnitSet).subscribe(
                  (response: Library) => {
                    console.log('Library submitted successfully');
                    this.isLoading = false;
                    this.store.dispatch(LibrarySaved());
                    this.sweetAlertService.showSuccessMessage('Saved!');
                    this.updateLibrary();
                  },
                  (error) => {
                    console.error('Error submitting library', error);
                    this.isLoading = false;
                    this.sweetAlertService.showErrorMessage('Error submitting library');
                  }
                );
              });
            
            } else if (result.isDenied) {
              this.sweetAlertService.showInfoMessage('Changes are not saved');
            }
          }
        });
      });
    })
  };

  // Function to generate the change table
  private generateModificationsTableHTMLString(library: any): string {
    let changeTable = '<table>';

    // Header row
    changeTable += '<tr><th>Before</th><th>After</th></tr>';

    // Added rows
    library.modifications.added.forEach((row: ExtendedRow) => {
      if (!Array.from(library.selectedRowsIdsToDelete).some(id => id === row.id))
        changeTable += '<tr style="border: 1px solid black;"><td style="padding: 10px;">None (This row was added)</td><td style="padding: 10px;">' + this.generateRowTableHTMLString(row) + '</td></tr>';
    });

    // Modified rows
    library.modifications.modified.forEach((row: ExtendedRow) => {
      var initialRow = library.initialRows.find((r: ExtendedRow) => r.id === row.id);
      if (initialRow && !Array.from(library.selectedRowsIdsToDelete).some(id => id === row.id)) {        
        changeTable += '<tr style="border: 1px solid black;"><td style="padding: 10px;">' + this.generateRowTableHTMLString(initialRow, row) + '</td><td style="padding: 10px;">' + this.generateRowTableHTMLString(row, initialRow) + '</td></tr>';
      }
    });

    // Deleted rows
    library.selectedRowsIdsToDelete.forEach((rowId: number) => {
      const initialRow = library.initialRows.find((r: ExtendedRow) => r.id === rowId);
      const currentRow = library.allowedRows.find((r: ExtendedRow) => r.id === rowId);
      if (initialRow) {
        changeTable += '<tr style="border: 1px solid black;"><td style="padding: 10px;">' + this.generateRowTableHTMLString(currentRow) + '</td><td style="padding: 10px;">None (This row was deleted)</td></tr>';
      }
    });

    changeTable += '</table>';

    if (library.modifications.added.length === 0 && library.modifications.modified.length === 0 && library.selectedRowsIdsToDelete.length === 0) {
      changeTable = '<p>No changes were made</p>';
    }

    return changeTable;
  }

  private generateRowTableHTMLString(row: ExtendedRow, oldRow: ExtendedRow | undefined = undefined): string {
    let rowTable = '<table style="border: 1px solid black; margin: 0px;">';

    for (const [key, value] of Object.entries(row)) {
      let backgroundColor = '';
      if (oldRow && (oldRow[key] === undefined || oldRow[key] !== value)) {
        backgroundColor = 'background-color: #faecbd;';
      }
      if (!['id', 'order'].includes(key)) {
        rowTable += `<tr style="border: 1px solid black;"><td style="padding: 5px;${backgroundColor}">${key}</td><td style="padding: 5px;${backgroundColor}">${value}</td></tr>`;
      }
    }
    rowTable += '</table>';

    return rowTable
  }

  public enableColumnForEdit(): boolean { //some columns, only admins can edit
    return this.libraryService.isAdmin() || this.libraryService.isSuperAdmin();
  }
}
